import { useEffect, useState } from "react";
import { Plus, X, TrendingUp, Trash2, Pencil, WifiOff, Download, RefreshCw } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { saveOfflineLead, getOfflineLeads, clearOfflineLeads, isOnline } from "@/lib/offlineLeads";
import { SiteButton } from "@/components/brand/SiteButton";

const STATUS_BADGE = {
  new: "border-brand-azure/40 bg-brand-azure/10 text-brand-azure",
  contacted: "border-yellow-400/40 bg-yellow-400/10 text-yellow-300",
  qualified: "border-brand-emerald/40 bg-brand-emerald/10 text-brand-emerald",
  proposal: "border-brand-mist/40 bg-brand-mist/10 text-brand-mist",
  won: "border-brand-emerald/60 bg-brand-emerald/20 text-brand-emerald",
  lost: "border-red-400/40 bg-red-400/10 text-red-400",
};
const STATUSES = ["new", "contacted", "qualified", "proposal", "won", "lost"];
const SERVICE_TYPES = ["general", "seo", "web-development", "mobile", "ai", "design"];

const LeadForm = ({ team, lead, onClose, onSaved }) => {
  const [f, setF] = useState(lead || { name: "", email: "", phone: "", company: "", service_interest: "general", status: "new", value: "", source: "manual", assigned_to: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = { ...f, value: f.value ? Number(f.value) : null, assigned_to: f.assigned_to || null };
      if (lead?.id) await api.patch(`/pms/leads/${lead.id}`, payload);
      else await api.post("/pms/leads", payload);
      onSaved();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
    setSaving(false);
  };

  const submitOffline = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = { ...f, value: f.value ? Number(f.value) : null, assigned_to: f.assigned_to || null };
      await saveOfflineLead(payload);
      onSaved();
    } catch (err) {
      setError("Failed to save offline.");
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-brand-black/70 backdrop-blur-sm sm:items-center sm:p-6" onClick={onClose} data-testid="lead-modal">
      <form onSubmit={submit} className="w-full max-w-lg space-y-3 rounded-t-2xl border border-brand-mist/15 bg-brand-charcoal p-6 sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-bold text-brand-snow">{lead ? "Edit lead" : "New lead"}</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded-md p-1.5 text-brand-mist hover:text-brand-snow"><X size={18} /></button>
        </div>
        <input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Contact name" data-testid="lead-name"
          className="w-full rounded-lg border border-brand-mist/20 bg-brand-black/40 px-3.5 py-2.5 text-sm text-brand-snow focus:border-brand-emerald focus:outline-none" />
        <div className="grid grid-cols-2 gap-3">
          <input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Email" data-testid="lead-email"
            className="rounded-lg border border-brand-mist/20 bg-brand-black/40 px-3.5 py-2.5 text-sm text-brand-snow focus:border-brand-emerald focus:outline-none" />
          <input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="Phone" data-testid="lead-phone"
            className="rounded-lg border border-brand-mist/20 bg-brand-black/40 px-3.5 py-2.5 text-sm text-brand-snow focus:border-brand-emerald focus:outline-none" />
        </div>
        <input value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} placeholder="Company" data-testid="lead-company"
          className="w-full rounded-lg border border-brand-mist/20 bg-brand-black/40 px-3.5 py-2.5 text-sm text-brand-snow focus:border-brand-emerald focus:outline-none" />
        <div className="grid grid-cols-2 gap-3">
          <select value={f.service_interest} onChange={(e) => setF({ ...f, service_interest: e.target.value })} data-testid="lead-service"
            className="rounded-lg border border-brand-mist/20 bg-brand-black/40 px-3.5 py-2.5 text-sm text-brand-snow focus:border-brand-emerald focus:outline-none">
            {SERVICE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="lead-status"
            className="rounded-lg border border-brand-mist/20 bg-brand-black/40 px-3.5 py-2.5 text-sm text-brand-snow focus:border-brand-emerald focus:outline-none">
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <input type="number" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} placeholder="Value (optional)" data-testid="lead-value"
            className="rounded-lg border border-brand-mist/20 bg-brand-black/40 px-3.5 py-2.5 text-sm text-brand-snow focus:border-brand-emerald focus:outline-none" />
          <select value={f.assigned_to} onChange={(e) => setF({ ...f, assigned_to: e.target.value })} data-testid="lead-assigned"
            className="rounded-lg border border-brand-mist/20 bg-brand-black/40 px-3.5 py-2.5 text-sm text-brand-snow focus:border-brand-emerald focus:outline-none">
            <option value="">Unassigned</option>
            {team.map((m) => <option key={m.id} value={m.id}>{m.name || m.email}</option>)}
          </select>
        </div>
        <textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="Notes (optional)" rows={2} data-testid="lead-notes"
          className="w-full rounded-lg border border-brand-mist/20 bg-brand-black/40 px-3.5 py-2.5 text-sm text-brand-snow focus:border-brand-emerald focus:outline-none" />
        {error && <p className="text-sm text-red-400">{error}</p>}
        <div className="flex justify-end gap-3 pt-2">
          <SiteButton type="button" variant="ghost" onClick={onClose}>Cancel</SiteButton>
          {!lead && !isOnline() && (
            <SiteButton type="button" onClick={submitOffline} disabled={saving} data-testid="lead-save-offline" variant="ghost">
              <WifiOff size={14} className="mr-1.5 inline" /> Save offline
            </SiteButton>
          )}
          <SiteButton type="submit" disabled={saving} data-testid="lead-save">{saving ? "Saving…" : lead ? "Save changes" : "Add lead"}</SiteButton>
        </div>
      </form>
    </div>
  );
};

const AdminLeads = () => {
  const [offlineSaved, setOfflineSaved] = useState(false);
  const [pendingSync, setPendingSync] = useState(0);
  const [leads, setLeads] = useState(null);
  const [team, setTeam] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editLead, setEditLead] = useState(null);
  const [me, setMe] = useState(null);

  const load = () => {
    api.get("/pms/leads").then((r) => setLeads(r.data)).catch(() => setLeads([]));
    getOfflineLeads().then((p) => setPendingSync(p.length)).catch(() => {});
  };

  const syncOffline = async () => {
    if (!isOnline()) return;
    try {
      const pending = await getOfflineLeads();
      if (pending.length === 0) { setPendingSync(0); return; }
      for (const lead of pending) {
        const { _offline, _savedAt, ...payload } = lead;
        await api.post("/pms/leads", payload);
      }
      await clearOfflineLeads();
      setPendingSync(0);
      load();
    } catch (err) { console.warn("Sync failed:", err); }
  };
  useEffect(() => {
    load();
    api.get("/pms/team").then((r) => setTeam(r.data)).catch(() => {});
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {});
    const onOnline = () => syncOffline();
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const remove = async (lead) => {
    if (!window.confirm(`Mark "${lead.name}" as lost?`)) return;
    await api.delete(`/pms/leads/${lead.id}`);
    load();
  };

  const isAdmin = me?.role === "admin";

  return (
    <div className="space-y-6" data-testid="admin-leads">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-brand-snow">Sales Leads</h1>
          <p className="mt-1 text-sm text-brand-mist">Track and manage external sales leads through the pipeline.</p>
        </div>
        <div className="flex items-center gap-2">
          {pendingSync > 0 && (
            <button onClick={syncOffline} data-testid="sync-offline" className="flex items-center gap-1.5 rounded-full border border-brand-azure/40 bg-brand-azure/10 px-3 py-1.5 text-xs text-brand-azure hover:bg-brand-azure/20">
              <RefreshCw size={12} /> Sync {pendingSync} offline
            </button>
          )}
          <SiteButton onClick={() => { setEditLead(null); setShowForm(true); }} testid="lead-new-button"><Plus size={16} className="mr-1.5 inline" /> New lead</SiteButton>
        </div>
      </div>

      {leads === null && <p className="text-sm text-brand-mist">Loading…</p>}
      {leads !== null && leads.length === 0 && (
        <div className="rounded-2xl border border-dashed border-brand-mist/20 p-12 text-center" data-testid="leads-empty">
          <TrendingUp className="mx-auto text-brand-mist/40" size={32} />
          <p className="mt-3 text-sm text-brand-mist">No leads yet. Add your first lead to start the pipeline.</p>
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border border-brand-mist/12" data-testid="leads-table">
        <table className="w-full min-w-[640px] text-left">
          <thead>
            <tr className="border-b border-brand-mist/15 bg-brand-black/40 text-[11px] uppercase tracking-wider text-brand-mist">
              <th className="px-4 py-3">Contact</th><th className="px-4 py-3">Company</th><th className="px-4 py-3">Service</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Value</th><th className="px-4 py-3">Assigned</th><th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {(leads || []).map((l) => (
              <tr key={l.id} className="border-b border-brand-mist/10" data-testid={`lead-row-${l.id}`}>
                <td className="px-4 py-3"><p className="text-sm font-medium text-brand-snow">{l.name}</p><p className="text-xs text-brand-mist">{l.email || l.phone || "—"}</p></td>
                <td className="px-4 py-3 text-sm text-brand-mist">{l.company || "—"}</td>
                <td className="px-4 py-3 text-sm text-brand-mist">{l.service_interest}</td>
                <td className="px-4 py-3"><span className={`rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${STATUS_BADGE[l.status] || STATUS_BADGE.new}`}>{l.status}</span></td>
                <td className="px-4 py-3 text-sm text-brand-snow">{l.value ? `$${l.value}` : "—"}</td>
                <td className="px-4 py-3 text-sm text-brand-mist">{l.assigned_to_user ? (l.assigned_to_user.name || l.assigned_to_user.email) : "—"}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    <button onClick={() => { setEditLead(l); setShowForm(true); }} aria-label="Edit lead" data-testid={`lead-edit-${l.id}`} className="rounded-md p-1.5 text-brand-mist hover:text-brand-emerald"><Pencil size={14} /></button>
                    {isAdmin && <button onClick={() => remove(l)} aria-label="Delete lead" data-testid={`lead-delete-${l.id}`} className="rounded-md p-1.5 text-brand-mist hover:text-red-400"><Trash2 size={14} /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && <LeadForm team={team} lead={editLead} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
    </div>
  );
};

export default AdminLeads;
